/* ONEJAW pellet rain — the scroll-scrubbed frame engine (25 Sep 2026).
   The section pins a stage while the page scrolls a set distance; the scroll position picks a frame of a rendered sequence.
   Built the way Apple's scroll pieces are built: progress comes from the runway's position in the viewport, the media is fetched
   ahead of arrival (coarse to fine, first frame first), drawing happens on requestAnimationFrame with an eased frame cursor (never
   in the scroll event), and a decoded window of frames around the cursor (ImageBitmaps, decoded off the main thread) is kept ahead
   of the scroll direction so a draw is only ever a blit. Frames are kept compressed (Blobs); only the window is decoded.
   Two sets: desktop 16:9 and phone 4:5, chosen once at start. Markup contract: section[data-ojrn] with data-first-d / data-first-m
   (the URL of frame 001 of each set), data-count, .ojrn__runway, .ojrn__frame, canvas. */
(function () {
  'use strict';
  if (!('requestAnimationFrame' in window)) return;
  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HAS_BITMAP = 'createImageBitmap' in window;
  var AHEAD = 14, BEHIND = 6, KEEP = 26;       // decoded window: frames ahead of the cursor, behind it, and the eviction distance
  var FETCH_PAR = 6, DECODE_PAR = 3;
  var HOLD = 0.08;                             // the last 8 % of the runway holds the final frame (the pile at rest) before the stage unpins

  function pattern(first) {
    var m = /^(.*_)(\d{3})(\.[a-z0-9]+)(\?.*)?$/i.exec(first || '');
    if (!m) return null;
    return function (i) { var n = String(i + 1); while (n.length < 3) n = '0' + n; return m[1] + n + m[3] + (m[4] || ''); };
  }

  function init(sec) {
    if (sec.__ojrn) return; sec.__ojrn = true;
    var canvas = sec.querySelector('canvas'), runway = sec.querySelector('.ojrn__runway'), box = sec.querySelector('.ojrn__frame');
    var N = parseInt(sec.getAttribute('data-count') || '0', 10);
    if (!canvas || !runway || !box || N < 2 || REDUCE) return;
    var ctx = canvas.getContext('2d', { alpha: false }); if (!ctx) return;
    var useM = window.matchMedia('(max-width: 640px)').matches && sec.getAttribute('data-first-m');
    var src = pattern(useM ? sec.getAttribute('data-first-m') : sec.getAttribute('data-first-d')); if (!src) return;

    var blobs = new Array(N), bitmaps = new Array(N), decoding = new Array(N), fetched = 0, failed = 0;
    var cur = 0, target = 0, shown = -1, W = 0, H = 0, natW = 0, natH = 0, live = false, running = false, raf = 0, lastT = 0, dir = 1;

    // ---- fetch: coarse to fine, so a scrub is usable long before every frame is in; six at a time
    var order = [], seen = {};
    [16, 8, 4, 2, 1].forEach(function (s) { for (var i = 0; i < N; i += s) { if (!seen[i]) { seen[i] = 1; order.push(i); } } });
    if (!seen[N - 1]) order.push(N - 1);
    var queue = order.slice(), inflight = 0, started = false;
    function fetchOne(i) {
      return fetch(src(i), { mode: 'cors', credentials: 'omit' }).then(function (r) { if (!r.ok) throw new Error(r.status); return r.blob(); })
        .then(function (b) { blobs[i] = b; fetched++; })
        .catch(function () { return imageFallback(i); });
    }
    function imageFallback(i) {   // no CORS / no fetch: an Image element (drawn the old way) so the piece still works
      return new Promise(function (res) {
        var im = new Image(); im.decoding = 'async';
        im.onload = function () { blobs[i] = im; fetched++; res(); }; im.onerror = function () { blobs[i] = null; failed++; res(); }; im.src = src(i);
      });
    }
    function pump() {
      while (inflight < FETCH_PAR && queue.length) {
        var i = queue.shift(); inflight++;
        fetchOne(i).then(function () { inflight--; kick(); pump(); });
      }
    }
    function start() { if (started) return; started = true; pump(); }

    // ---- decode window: ImageBitmaps around the cursor, more ahead in the scroll direction; evict far ones
    var decodes = 0;
    function want(c) {
      var list = [Math.round(c)], k;
      for (k = 1; k <= AHEAD; k++) list.push(Math.round(c) + k * dir);
      for (k = 1; k <= BEHIND; k++) list.push(Math.round(c) - k * dir);
      return list;
    }
    function decodeOne(i) {
      var b = blobs[i]; if (!b || bitmaps[i] || decoding[i]) return;
      decoding[i] = true; decodes++;
      var p = (HAS_BITMAP && b instanceof Blob) ? createImageBitmap(b) : (b instanceof Blob ? blobToImage(b) : Promise.resolve(b));
      p.then(function (bm) { bitmaps[i] = bm; if (!natW) { natW = bm.width || bm.naturalWidth; natH = bm.height || bm.naturalHeight; } })
       .catch(function () { blobs[i] = null; failed++; })
       .then(function () { decoding[i] = false; decodes--; kick(); });
    }
    function blobToImage(b) {
      return new Promise(function (res, rej) { var u = URL.createObjectURL(b), im = new Image(); im.onload = function () { URL.revokeObjectURL(u); res(im); }; im.onerror = function () { URL.revokeObjectURL(u); rej(); }; im.src = u; });
    }
    function fill(c) {
      var list = want(c);
      for (var k = 0; k < list.length && decodes < DECODE_PAR; k++) { var i = list[k]; if (i >= 0 && i < N) decodeOne(i); }
      var lo = Math.round(c) - KEEP, hi = Math.round(c) + KEEP;
      for (var j = 0; j < N; j++) { if (bitmaps[j] && (j < lo || j > hi) && j !== 0) { if (bitmaps[j].close) bitmaps[j].close(); bitmaps[j] = undefined; } }
    }
    function nearest(i) {
      if (bitmaps[i]) return i;
      for (var d = 1; d < N; d++) { if (i - d >= 0 && bitmaps[i - d]) return i - d; if (i + d < N && bitmaps[i + d]) return i + d; }
      return -1;
    }

    // ---- draw: cover-fit blit; backing store at the device's pixel ratio, never larger than the frame itself
    function size() {
      var bw = box.clientWidth, bh = box.clientHeight; if (!bw || !bh) return false;
      var dpr = Math.min(window.devicePixelRatio || 1, 2); if (natW) dpr = Math.min(dpr, natW / bw);
      var w = Math.round(bw * dpr), h = Math.round(bh * dpr);
      if (w !== canvas.width || h !== canvas.height) { canvas.width = w; canvas.height = h; shown = -1; }
      W = w; H = h; return true;
    }
    function draw(i) {
      var im = bitmaps[i]; if (!im || !size()) return;
      var iw = im.width || im.naturalWidth, ih = im.height || im.naturalHeight, s = Math.max(W / iw, H / ih), dw = iw * s, dh = ih * s;
      ctx.drawImage(im, (W - dw) / 2, (H - dh) / 2, dw, dh); shown = i; sec.setAttribute('data-frame', String(i));
      if (!live) { live = true; sec.classList.add('is-live'); }
    }
    function progress() {
      var r = runway.getBoundingClientRect(), total = r.height - window.innerHeight; if (total <= 0) return 1;
      var p = -r.top / total; return p < 0 ? 0 : (p > 1 ? 1 : p);
    }

    // ---- the loop: runs while the cursor is settling or a frame is missing, then sleeps until the next scroll
    function tick(t) {
      raf = 0; var dt = lastT ? Math.min(50, t - lastT) : 16.7; lastT = t;
      var p = progress(); target = Math.min(1, p / (1 - HOLD)) * (N - 1);
      var diff = target - cur; if (Math.abs(diff) > 0.01) dir = diff > 0 ? 1 : -1;
      var k = 1 - Math.pow(0.80, dt / 16.7);            // eased cursor: closes 20 % of the gap per 60 Hz frame, whatever the refresh rate
      cur = Math.abs(diff) < 0.05 ? target : cur + diff * k;
      if (p >= 0.96) sec.classList.add('is-done'); else sec.classList.remove('is-done');
      fill(cur);
      var want_ = Math.round(cur), i = nearest(want_);
      if (i >= 0 && i !== shown) draw(i);
      running = Math.abs(target - cur) >= 0.05 || i !== want_ || (i < 0);
      if (running) raf = requestAnimationFrame(tick); else lastT = 0;
    }
    function kick() { if (!raf) raf = requestAnimationFrame(tick); }

    // ---- wire up: fetch two screens ahead of arrival; scroll and resize only wake the loop
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) { es.forEach(function (e) { if (e.isIntersecting) { start(); kick(); io.disconnect(); } }); }, { rootMargin: '200% 0px 200% 0px' });
      io.observe(sec);
    } else { start(); kick(); }
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', function () { shown = -1; kick(); }, { passive: true });
    document.addEventListener('visibilitychange', function () { if (!document.hidden) kick(); });
    sec.__ojrnState = function () { return { fetched: fetched, failed: failed, decoded: bitmaps.filter(Boolean).length, shown: shown, cur: cur, target: target }; };
  }

  function boot() { var all = document.querySelectorAll('[data-ojrn]'); for (var i = 0; i < all.length; i++) init(all[i]); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
